import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { WeaveThemeContext } from './WeaveThemeContext';
import { INFO360_THEMES } from '../theme.types';
import { useThemeVariables } from '../../../../utils';
import { ThemeContextValue } from '@weave-design/theme-context';
import { weaveThemes } from '../weave.types';
import {
	useSelectFeaturesLoaded,
	useSelectUserSettings,
} from '../../../../selectors';
import cookies from 'browser-cookies';
import { useDispatch } from 'react-redux';
import { updateUserSettings } from '../../../../actions/userSettings.actions';

// helper functions
const setThemeCookie = (theme: INFO360_THEMES) => {
	cookies.set('theme', theme, {
		domain: window.location.hostname,
		expires: 365,
		samesite: 'Lax',
		secure: true,
	});
};
const getThemeCookie = () => {
	return cookies.get('theme') as INFO360_THEMES;
};

const userThemeMap: { [key: string]: INFO360_THEMES } = {
	light: INFO360_THEMES.Light,
	dark: INFO360_THEMES.Dark,
	legacy: INFO360_THEMES.Classic,
};

// WeaveThemeProvider component

interface WeaveThemeProviderProps {
	defaultTheme: INFO360_THEMES;
	children: ReactNode;
}
const WeaveThemeProvider = ({
	defaultTheme,
	children,
}: WeaveThemeProviderProps) => {
	const { displayThemePicker } = useThemeVariables();
	const { selectedTheme: userSettingsTheme } = useSelectUserSettings();
	const isLoading = !useSelectFeaturesLoaded();
	const channel = new BroadcastChannel('weave-theme-channel');
	const dispatch = useDispatch();

	const [theme, setWeaveTheme] = useState<INFO360_THEMES>(
		getThemeCookie() ?? defaultTheme,
	);
	const [weaveThemeConsumer, setWeaveThemeConsumer] =
		useState<ThemeContextValue>(weaveThemes[theme]);

	const isWeaveTheme = theme !== INFO360_THEMES.Classic;

	const setTheme = useCallback(
		(newTheme: INFO360_THEMES, updateStorage = true) => {
			setWeaveTheme(newTheme);
			setWeaveThemeConsumer(weaveThemes[newTheme]);
			if (updateStorage) {
				dispatch(
					updateUserSettings({
						selectedTheme: Object.keys(userThemeMap).find(
							key => userThemeMap[key] === newTheme,
						),
					}),
				);
				setThemeCookie(newTheme);
				channel?.postMessage({
					type: 'THEME_CHANGE',
					theme: newTheme,
				});
			}
		},
		[weaveThemes, channel],
	);

	// Effects
	// (User settings are now requested by useInitialization (runDataStep).)
	//Listen for theme changes from other tabs
	useEffect(() => {
		const handleThemeChange = (event: MessageEvent) => {
			if (event.data?.type === 'THEME_CHANGE') {
				setTheme(event.data.theme, false);
			}
		};
		channel.addEventListener('message', handleThemeChange);
		return () => {
			channel.removeEventListener('message', handleThemeChange);
			channel.close();
		};
	}, []);
	// Initialize
	useEffect(() => {
		let selectedTheme = getThemeCookie() ?? defaultTheme;
		if (userSettingsTheme) {
			selectedTheme = userThemeMap[userSettingsTheme];
		}
		const displayDefault =
			(isLoading && !selectedTheme) ||
			(!isLoading && !displayThemePicker);
		const themeValue = displayDefault ? defaultTheme : selectedTheme;

		setTheme(themeValue, !isLoading);
	}, [displayThemePicker, isLoading, userSettingsTheme]);
	// End Effects

	// Render
	return (
		<WeaveThemeContext.Provider
			value={{
				theme,
				setTheme,
				weaveThemeConsumer,
				isWeaveTheme,
			}}>
			{children}
		</WeaveThemeContext.Provider>
	);
};

export default WeaveThemeProvider;
